// การใช้งานจริง — นับจากจำนวน request ที่ผ่านการยืนยันตัวตน ไม่ใช่จำนวนครั้งที่ล็อกอิน
//
// "เข้าระบบ 40 ครั้ง" กับ "ใช้งานระบบจริง" เป็นคนละเรื่อง แท็บนี้ตอบว่าใครใช้จริง
// โมดูลไหนมีคนใช้ และช่วงเวลาไหนคนน้อยพอจะสำรองข้อมูล/ปิดปรับปรุงได้
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import axiosInstance from "../../services/axiosInterceptor";
import { RANGE_OPTIONS, StatTile, formatDate, formatHour, num } from "./shared";

const API = process.env.REACT_APP_API;

const EMPTY = {
  totals: {}, byDate: [], byHour: [], byTeacher: [],
  topEndpoints: [], errorEndpoints: [], slowEndpoints: [],
};

// 1,240 ms -> "1.2 วิ" / 340 ms -> "340 ms" — วินาทีอ่านง่ายกว่าเมื่อเกินหลักพัน
const formatMs = (ms) => {
  const n = num(ms);
  if (!n) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(1)} วิ` : `${n} ms`;
};

// เขียว < 1 วิ, เหลือง < 3 วิ, แดงเกินนั้น — 3 วิคือจุดที่คนเริ่มรู้สึกว่า "ค้าง"
const msVariant = (ms) => (num(ms) < 1000 ? 'success' : num(ms) < 3000 ? 'warning' : 'danger');

// รีเฟรชรายชื่อคนที่กำลังใช้งานเองทุก 30 วิ — ตัวเลขนี้มีประโยชน์ก็ต่อเมื่อมันสด
// (ใช้ตอบว่า "restart ตอนนี้ได้ไหม" ซึ่งข้อมูลเก่า 5 นาทีตอบไม่ได้)
const ONLINE_REFRESH_MS = 30 * 1000;

export default function ActivityTab() {
  const [data, setData]     = useState(EMPTY);
  const [days, setDays]     = useState(30);
  const [online, setOnline] = useState({ count: 0, users: [] });
  const [busy, setBusy]     = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    axiosInstance
      .get(`${API}/admin/usage/activity`, { params: { days } })
      .then((res) => setData({ ...EMPTY, ...res.data }))
      .catch(() => setData(EMPTY))
      .finally(() => setBusy(false));
  }, [days]);

  const loadOnline = useCallback(() => {
    axiosInstance
      .get(`${API}/admin/usage/online`)
      .then((res) => setOnline(res.data || { count: 0, users: [] }))
      .catch(() => setOnline({ count: 0, users: [] }));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    loadOnline();
    const timer = setInterval(loadOnline, ONLINE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadOnline]);

  const requests = num(data.totals?.requests);
  const activeDays = data.byDate.length;

  const peakHours = useMemo(
    () => [...data.byHour].sort((a, b) => num(b.requests) - num(a.requests)).slice(0, 5),
    [data.byHour]
  );

  // เรียงวันใหม่สุดขึ้นก่อน — server ส่งมาเรียงจากเก่าไปใหม่
  const recentDays = useMemo(() => [...data.byDate].reverse(), [data.byDate]);

  return (
    <React.Fragment>
      <Card className="card-one mb-3">
        <Card.Body className="d-flex flex-wrap align-items-center gap-2 py-3">
          <div className="me-3">
            <div className="text-secondary fs-xs">กำลังใช้งานอยู่ตอนนี้</div>
            <h3 className={`mb-0 text-${online.count ? 'success' : 'secondary'}`}>{online.count} คน</h3>
          </div>
          <div className="d-flex flex-wrap gap-1 align-items-center">
            {online.users.length === 0
              ? <span className="text-secondary fs-sm">ไม่มีใครใช้งานอยู่ — restart ได้เลย</span>
              : online.users.map((u) => (
                  <Badge bg="light" text="dark" key={u.t_id} className="fw-normal">{u.t_name}</Badge>
                ))}
          </div>
          <div className="ms-auto text-secondary fs-xs text-end">
            นับจาก request ใน 5 นาทีล่าสุด<br />
            เริ่มนับใหม่ทุกครั้งที่ restart เซิร์ฟเวอร์
          </div>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
        <Form.Select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ maxWidth: 180 }}>
          {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Form.Select>
        {/* รีเฟรชทั้งสถิติและการ์ด "กำลังใช้งานอยู่" พร้อมกัน ไม่งั้นกดแล้วตัวเลขบนสุดยังค้างของเก่า */}
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => { load(); loadOnline(); }}
          disabled={busy}
        >
          <i className="ri-refresh-line me-1"></i>รีเฟรช
        </Button>
      </div>

      <Row className="g-2 g-lg-3 mb-3">
        <StatTile label="จำนวนการเรียกใช้งาน" value={requests.toLocaleString()} variant="dark" />
        <StatTile label="ผู้ใช้ที่ใช้งานจริง"  value={num(data.totals?.users).toLocaleString()} variant="success" />
        <StatTile label="วันที่มีการใช้งาน"    value={activeDays.toLocaleString()} variant="info" />
        <StatTile
          label="เฉลี่ยต่อวันที่ใช้งาน"
          value={activeDays ? Math.round(requests / activeDays).toLocaleString() : '—'}
          variant="secondary"
        />
      </Row>

      <Row className="g-2 g-lg-3 mb-3">
        <Col lg={7}>
          <Card className="card-one h-100">
            <Card.Header><Card.Title as="h6" className="mb-0">การใช้งานรายวัน</Card.Title></Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 340, overflowY: 'auto' }}>
              {recentDays.length === 0 ? (
                <div className="text-center text-secondary py-4 fs-sm">ยังไม่มีข้อมูล</div>
              ) : (
                <Table hover className="mb-0">
                  <thead className="table-secondary">
                    <tr>
                      <th>วันที่</th>
                      <th className="text-end" style={{ width: 130 }}>ผู้ใช้</th>
                      <th className="text-end" style={{ width: 150 }}>การเรียกใช้งาน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentDays.map((d) => (
                      <tr key={d.date}>
                        <td className="text-nowrap">{formatDate(d.date)}</td>
                        <td className="text-end">{num(d.users).toLocaleString()}</td>
                        <td className="text-end">{num(d.requests).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={5}>
          <Card className="card-one h-100">
            <Card.Header>
              <Card.Title as="h6" className="mb-0">ช่วงเวลาที่ใช้งานหนักที่สุด</Card.Title>
              <span className="text-secondary fs-xs">ใช้เลือกเวลาสำรองข้อมูลหรือปิดปรับปรุงระบบ</span>
            </Card.Header>
            <Card.Body className="p-0">
              {peakHours.length === 0 ? (
                <div className="text-center text-secondary py-4 fs-sm">ยังไม่มีข้อมูล</div>
              ) : (
                <Table hover className="mb-0">
                  <tbody>
                    {peakHours.map((h) => (
                      <tr key={h.hour}>
                        <td>{formatHour(h.hour)}</td>
                        <td className="text-end text-nowrap">{num(h.requests).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-2 g-lg-3">
        <Col lg={6}>
          <Card className="card-one h-100">
            <Card.Header><Card.Title as="h6" className="mb-0">บุคลากรที่ใช้งานมากที่สุด</Card.Title></Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 420, overflowY: 'auto' }}>
              <Table hover className="mb-0">
                <thead className="table-secondary">
                  <tr>
                    <th style={{ width: 100 }}>รหัสผู้ใช้</th>
                    <th>ชื่อ - นามสกุล</th>
                    <th className="text-end" style={{ width: 90 }}>วันที่ใช้</th>
                    <th className="text-end" style={{ width: 110 }}>เรียกใช้งาน</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byTeacher.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-secondary py-4">ยังไม่มีข้อมูล</td></tr>
                  ) : data.byTeacher.map((r) => (
                    <tr key={r.tb_teacher_t_id}>
                      <td className="align-middle fw-semibold">{r.t_idnumber}</td>
                      <td className="align-middle">{r.t_name}</td>
                      <td className="align-middle text-end">{num(r.active_days)}</td>
                      <td className="align-middle text-end">{num(r.requests).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="card-one h-100">
            <Card.Header>
              <Card.Title as="h6" className="mb-0">หน้าที่ถูกเรียกใช้มากที่สุด</Card.Title>
              <span className="text-secondary fs-xs">เลข id ในเส้นทางถูกยุบเป็น :id แล้ว</span>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 420, overflowY: 'auto' }}>
              <Table hover className="mb-0">
                <thead className="table-secondary">
                  <tr>
                    <th>เส้นทาง</th>
                    <th className="text-end" style={{ width: 100 }}>เรียกใช้งาน</th>
                    <th className="text-end" style={{ width: 90 }}>เฉลี่ย</th>
                    <th className="text-end" style={{ width: 80 }}>ขัดข้อง</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topEndpoints.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-secondary py-4">ยังไม่มีข้อมูล</td></tr>
                  ) : data.topEndpoints.map((r) => (
                    <tr key={r.path}>
                      <td className="align-middle fs-sm" style={{ wordBreak: 'break-all' }}>{r.path}</td>
                      <td className="align-middle text-end">{num(r.requests).toLocaleString()}</td>
                      <td className={`align-middle text-end text-${msVariant(r.avg_ms)}`}>{formatMs(r.avg_ms)}</td>
                      <td className="align-middle text-end">
                        {num(r.errors) > 0
                          ? <Badge bg="danger">{num(r.errors)}</Badge>
                          : <span className="text-secondary">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="card-one mt-3">
        <Card.Header>
          <Card.Title as="h6" className="mb-0">หน้าที่ตอบสนองช้า</Card.Title>
          <span className="text-secondary fs-xs">
            เวลาฝั่งเซิร์ฟเวอร์เท่านั้น ไม่รวมเน็ตของผู้ใช้ — ถ้าครูบอกช้าแต่ตรงนี้เร็ว
            แปลว่าปัญหาอยู่ที่เครื่องหรือเน็ตปลายทาง · นับเฉพาะหน้าที่ถูกเรียกตั้งแต่ 10 ครั้งขึ้นไป
          </span>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th>เส้นทาง</th>
                  <th className="text-end" style={{ width: 110 }}>เฉลี่ย</th>
                  <th className="text-end" style={{ width: 130 }}>ช้าที่สุด</th>
                  <th className="text-end" style={{ width: 110 }}>เรียกใช้งาน</th>
                </tr>
              </thead>
              <tbody>
                {data.slowEndpoints.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-secondary py-4">
                    ยังไม่มีข้อมูลมากพอ
                  </td></tr>
                ) : data.slowEndpoints.map((r) => (
                  <tr key={r.path}>
                    <td className="align-middle fs-sm" style={{ wordBreak: 'break-all' }}>{r.path}</td>
                    <td className="align-middle text-end">
                      <Badge bg={msVariant(r.avg_ms)}>{formatMs(r.avg_ms)}</Badge>
                    </td>
                    <td className="align-middle text-end">{formatMs(r.max_ms)}</td>
                    <td className="align-middle text-end">{num(r.requests).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Card className="card-one mt-3">
        <Card.Header>
          <Card.Title as="h6" className="mb-0">หน้าที่ระบบขัดข้อง (5xx)</Card.Title>
          <span className="text-secondary fs-xs">
            เรียงตามจำนวนครั้งที่ขัดข้อง ไม่ใช่ตามจำนวนคนใช้ — ครูส่วนใหญ่ไม่แจ้ง แค่เลิกใช้ฟีเจอร์นั้นไปเงียบๆ
          </span>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th>เส้นทาง</th>
                  <th className="text-end" style={{ width: 110 }}>ขัดข้อง</th>
                  <th className="text-end" style={{ width: 110 }}>เรียกทั้งหมด</th>
                  <th className="text-end" style={{ width: 110 }}>สัดส่วน</th>
                </tr>
              </thead>
              <tbody>
                {data.errorEndpoints.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-secondary py-4">
                    ไม่มีการขัดข้องในช่วงนี้
                  </td></tr>
                ) : data.errorEndpoints.map((r) => {
                  const requests = num(r.requests);
                  const errors   = num(r.errors);
                  const rate     = requests ? Math.round((errors / requests) * 100) : 0;
                  return (
                    <tr key={r.path}>
                      <td className="align-middle fs-sm" style={{ wordBreak: 'break-all' }}>{r.path}</td>
                      <td className="align-middle text-end">
                        <Badge bg={rate >= 50 ? 'danger' : 'warning'}>{errors.toLocaleString()}</Badge>
                      </td>
                      <td className="align-middle text-end">{requests.toLocaleString()}</td>
                      <td className="align-middle text-end">{rate}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </React.Fragment>
  );
}
